import { useCallback, useEffect, useRef, useState } from "react"
import {
  getDemoFixture,
  isDemoMode,
} from "@/v2-status-client/status/status.demo"
import { normalizeStatusResponse } from "@/v2-status-client/status/status.normalize"
import type { PublicStatusResponse } from "@/v2-status-client/status/status.types"

// Default polling cadence. 60 s is fast enough that a customer
// who lands on the page during an outage sees fresh data within a
// minute, and slow enough that a CDN edge can absorb the traffic
// without us having to think about rate-limiting. The page also
// short-cache the JSON for 30 s server-side.
const DEFAULT_POLL_INTERVAL_MS = 60_000

// Endpoint the page hits. Always relative -- both in dev (Vite
// proxies `/public/*` to localhost:8092 via vite.config.ts) and in
// prod (nginx in the container proxies `/public/*` to STATUS_API_URL,
// see ../nginx.conf). Single-origin design avoids a CORS preflight
// on every poll and lets the CDN in front of status.placepoint.no
// cache the page and the JSON under one hostname.
const ENDPOINT = "/public/status"

export type StatusQueryState = {
  data: PublicStatusResponse | null
  // True while the very first response is still in flight (no
  // data, no error reported back yet). Drives the initial-load
  // skeleton; flips false the moment we either have data or an
  // explicit error to render.
  loading: boolean
  // True while *any* fetch is in flight, even when we're already
  // showing data. Used by the footer pip and the manual Retry
  // button to convey "checking again..." without wiping the page.
  isRefreshing: boolean
  error: string | null
  // Wall-clock timestamp the last successful response was received
  // at. Drives the "Updated 12 s ago" pip in the page footer.
  lastSuccessAt: Date | null
}

export type StatusQueryResult = StatusQueryState & {
  // Force an immediate re-poll outside the regular cadence. Wired
  // into the Retry button on the error fallback so customers
  // don't have to wait the full poll interval after a transient
  // failure. No-op in demo mode (the fixture never fails).
  refetch: () => void
}

export type UseStatusQueryOptions = {
  pollIntervalMs?: number
}

// useStatusQuery polls the public status JSON endpoint and exposes
// the latest response, with a small loading/error envelope around
// it. Deliberately a plain `fetch` -- no Apollo, no GraphQL -- so
// the public bundle stays tiny and doesn't drag a query cache it
// doesn't need into the public surface area.
export function useStatusQuery(
  opts: UseStatusQueryOptions = {},
): StatusQueryResult {
  const { pollIntervalMs = DEFAULT_POLL_INTERVAL_MS } = opts

  const [state, setState] = useState<StatusQueryState>(() => {
    // Demo mode short-circuits the polling lifecycle entirely so a
    // ?demo=1 link is review-friendly even when the public API is
    // not running locally. Same response shape so the rest of the
    // page renders identically.
    if (isDemoMode()) {
      return {
        // Demo path goes through the same normaliser as the live
        // wire so any future shape drift in the demo fixture is
        // healed at the boundary instead of crashing a section of
        // the page.
        data: normalizeStatusResponse(getDemoFixture()),
        loading: false,
        isRefreshing: false,
        error: null,
        lastSuccessAt: new Date(),
      }
    }
    return {
      data: null,
      loading: true,
      isRefreshing: true,
      error: null,
      lastSuccessAt: null,
    }
  })

  // Imperative kicker exposed to React via `refetch`. We store it
  // in a ref so we can return a stable callback identity even
  // though the underlying poll loop is owned by `useEffect` and
  // recreated on every option change.
  const refetchRef = useRef<() => void>(() => {})

  useEffect(() => {
    if (isDemoMode()) {
      // Demo mode: refetch is a no-op so the Retry button still
      // behaves predictably even under ?demo=1.
      refetchRef.current = () => {}
      return
    }
    const controller = new AbortController()
    let timer: ReturnType<typeof setTimeout> | null = null
    let cancelled = false

    function clearPendingTimer() {
      if (timer != null) {
        clearTimeout(timer)
        timer = null
      }
    }

    async function fetchOnce() {
      // Mark the in-flight refresh so the footer pip + Retry
      // button can spin without wiping the page underneath.
      setState((s) => ({ ...s, isRefreshing: true }))
      try {
        const res = await fetch(ENDPOINT, {
          method: "GET",
          signal: controller.signal,
          headers: { Accept: "application/json" },
        })
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`)
        }
        // Normalise rather than cast: the API and the bundle ship
        // independently, so we treat the wire payload as untrusted
        // (missing top-level arrays, missing platformId on a
        // category, missing summary fields on a stale build, etc)
        // and fill in safe defaults at the boundary. See
        // `status.normalize.ts` for the full contract.
        const raw = (await res.json()) as unknown
        const json = normalizeStatusResponse(raw)
        if (cancelled) return
        setState({
          data: json,
          loading: false,
          isRefreshing: false,
          error: null,
          lastSuccessAt: new Date(),
        })
      } catch (error) {
        if (cancelled) return
        if ((error as { name?: string }).name === "AbortError") return
        setState((s) => ({
          // Keep the last good `data` on screen so a transient
          // network blip doesn't wipe the page; surface the error
          // alongside as a soft "couldn't refresh" warning.
          data: s.data,
          loading: false,
          isRefreshing: false,
          error: (error as Error).message || "Network error",
          lastSuccessAt: s.lastSuccessAt,
        }))
      } finally {
        // eslint-disable-next-line no-unsafe-finally
        if (cancelled) return
        clearPendingTimer()
        timer = setTimeout(fetchOnce, pollIntervalMs)
      }
    }

    // Kick the cadence and wire up the refetch entry point.
    refetchRef.current = () => {
      if (cancelled) return
      clearPendingTimer()
      void fetchOnce()
    }
    void fetchOnce()
    return () => {
      cancelled = true
      controller.abort()
      clearPendingTimer()
      refetchRef.current = () => {}
    }
  }, [pollIntervalMs])

  // Stable callback identity across renders so consumers can pass
  // `refetch` straight into `<Button onClick=...>` without React
  // tearing the prop on every poll-driven re-render.
  const refetch = useCallback(() => refetchRef.current(), [])

  return { ...state, refetch }
}
